import * as React from "react";
import Image, { StaticImageData } from "next/legacy/image";
import FacebookIcon from "@mui/icons-material/Facebook";
import { pipe } from "fp-ts/function";
import { option } from "fp-ts";
import {
    AppBar,
    Box,
    Container,
    Grid,
    Hidden,
    IconButton,
    Link,
    ThemeProvider,
    Toolbar,
    Typography,
} from "@mui/material";
import { UrlObject } from "url";
import {
    calendar,
    contact,
    index,
    rules,
    segmentDetailRoute,
    seriesListRoute,
    SubdomainQuery,
} from "../../../routes/org";
import { ExternalLink, InternalLink } from "../../common/Link";
import { LogoXathlo } from "../../LogoXathlo";
import { NextSeoProps } from "next-seo/lib/types";
import { NextSeo } from "next-seo";
import { createXathloTheme } from "../../../theme/light";
import ceskaVesLogo from "../../../../public/cvk/ceskaves.png";
import cvkLogo from "../../../../public/cvk/logo.png";
import { CvkLogo } from "./CvkLogo";

interface Props extends SubdomainQuery {
    children: React.ReactNode;
    seo?: NextSeoProps;
}

const snekTheme = createXathloTheme({
    palette: {
        primary: {
            main: "#008983",
            light: "#4eb9b2",
            dark: "#005b56",
        },
    },
});

const CvkLayout = (p: Props) => {
    return (
        <ThemeProvider theme={snekTheme}>
            {p.seo == null ? null : <NextSeo {...p.seo} />}
            <Box
                sx={{
                    // backgroundColor: (theme) => theme.palette.grey[200],
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh",
                }}
            >
                <AppBar
                    position="static"
                    color="transparent"
                    elevation={4}
                    sx={{
                        pt: 1,
                    }}
                >
                    <Container maxWidth={"lg"}>
                        <Toolbar
                            sx={{
                                flexWrap: "wrap",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box>
                                <CvkLogo subdomain={p.subdomain} />
                            </Box>

                            <Grid
                                container
                                component={"nav"}
                                sx={{ width: "auto", pt: 1, pb: 2 }}
                                spacing={3}
                            >
                                <NavLink
                                    label={"Archiv výsledků"}
                                    route={calendar.route({
                                        subdomain: p.subdomain,
                                    })}
                                />
                                <NavLink
                                    label={"Trať 9,1km"}
                                    route={segmentDetailRoute.route({
                                        subdomain: p.subdomain,
                                        segmentId: 90,
                                    })}
                                />
                                <NavLink
                                    label={"Trať 3,9km"}
                                    route={segmentDetailRoute.route({
                                        subdomain: p.subdomain,
                                        segmentId: 91,
                                    })}
                                />
                                <NavLink
                                    label={"Trať 2,2km"}
                                    route={segmentDetailRoute.route({
                                        subdomain: p.subdomain,
                                        segmentId: 92,
                                    })}
                                />
                                <NavLink
                                    label={"Trať 200m"}
                                    route={segmentDetailRoute.route({
                                        subdomain: p.subdomain,
                                        segmentId: 93,
                                    })}
                                />
                                <NavLink
                                    label={"Trať 50m"}
                                    route={segmentDetailRoute.route({
                                        subdomain: p.subdomain,
                                        segmentId: 94,
                                    })}
                                />
                            </Grid>
                            <Hidden mdDown>
                                <Box>
                                    <IconButton
                                        target={"_blank"}
                                        href={
                                            "https://www.facebook.com/ceskoveskykros"
                                        }
                                    >
                                        <FacebookIcon />
                                    </IconButton>
                                </Box>
                            </Hidden>
                        </Toolbar>
                    </Container>
                </AppBar>
                <Box
                    sx={{
                        my: 4,
                    }}
                >
                    {p.children}
                </Box>
                <Box
                    component="footer"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.background.default,
                        marginTop: "auto",
                        p: 2,
                        borderTop: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Container maxWidth="xl">
                        <Typography variant={"h5"}>Partneři:</Typography>
                        <Grid
                            container
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            spacing={1}
                        >
                            <Partner
                                logo={ceskaVesLogo}
                                web={"http://cves.cz/"}
                                name={"Česká Ves"}
                                height={60}
                            />
                            <PartnerGridItem>
                                <ExternalLink href={"https://xathlo.com"}>
                                    <LogoXathlo />
                                </ExternalLink>
                            </PartnerGridItem>
                        </Grid>
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default CvkLayout;

interface PartnerProps {
    logo: StaticImageData;
    web: string;
    name: string;
    height?: number;
}

interface Rect {
    width: number;
    height: number;
}

const Partner = (p: PartnerProps) => {
    const imageSize: Partial<Rect> = pipe(
        option.fromNullable(p.height),
        option.fold(
            () => p.logo,
            (h) => {
                const w2h = p.logo.width / p.logo.height;
                const width = w2h * h;
                return { width, height: p.height };
            }
        )
    );

    return (
        <PartnerGridItem>
            <ExternalLink href={p.web}>
                <Image
                    src={p.logo}
                    alt={p.name}
                    width={imageSize.width}
                    height={imageSize.height}
                />
            </ExternalLink>
        </PartnerGridItem>
    );
};

interface PartnerGridItemProps {
    children: React.ReactNode;
}

const PartnerGridItem = (p: PartnerGridItemProps) => {
    return (
        <Grid
            item
            xs={12}
            sm={3}
            md={2}
            lg={1}
            flexGrow={1}
            sx={{ textAlign: "center" }}
        >
            {p.children}
        </Grid>
    );
};

interface NavLinkProps {
    label: string;
    route: UrlObject;
}

const NavLink = (p: NavLinkProps) => (
    <NavItem>
        <InternalLink href={p.route}>
            <Link variant="button" underline={"hover"}>
                {p.label}
            </Link>
        </InternalLink>
    </NavItem>
);

interface NavItemProps {
    children: React.ReactNode;
}

const NavItem = (props: NavItemProps) => <Grid item>{props.children}</Grid>;
